import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MapPin, Eye, EyeOff, User, Mail, Phone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'guest';
  const { login, register } = useAuth();
  
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: defaultRole,
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      navigateByRole(user.role);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(registerForm);
      toast.success('Account created successfully!');
      navigateByRole(user.role);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const navigateByRole = (role) => {
    switch (role) {
      case 'host':
        navigate('/host/dashboard');
        break;
      case 'admin':
        navigate('/admin/dashboard');
        break;
      default:
        navigate('/search');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20 bg-farmhouse-charcoal" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MapPin className="w-8 h-8 text-farmhouse-saddle" />
            <span className="font-serif text-2xl font-semibold text-farmhouse-cream">
              Barns & Pastures
            </span>
          </div>
          <p className="text-farmhouse-muted">
            {activeTab === 'login' ? 'Welcome back!' : 'Create your account'}
          </p>
        </div>

        {/* Auth Card */}
        <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md p-6 shadow-warm">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 bg-farmhouse-charcoal mb-6">
              <TabsTrigger 
                value="login" 
                className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
                data-testid="login-tab"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="register"
                className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
                data-testid="register-tab"
              >
                Sign Up
              </TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-farmhouse-cream">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-farmhouse-muted" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="form-input pl-10"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-farmhouse-cream">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="form-input pr-10"
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-farmhouse-muted hover:text-farmhouse-cream"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full" 
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
                </Button>
              </form>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="register-name" className="text-farmhouse-cream">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-farmhouse-muted" />
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="John Doe"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      className="form-input pl-10"
                      required
                      data-testid="register-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-email" className="text-farmhouse-cream">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-farmhouse-muted" />
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="you@example.com"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      className="form-input pl-10"
                      required
                      data-testid="register-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-phone" className="text-farmhouse-cream">Phone (Optional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-farmhouse-muted" />
                    <Input
                      id="register-phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={registerForm.phone}
                      onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                      className="form-input pl-10"
                      data-testid="register-phone"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-password" className="text-farmhouse-cream">Password</Label>
                  <div className="relative">
                    <Input
                      id="register-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      className="form-input pr-10"
                      required
                      minLength={6}
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-farmhouse-muted hover:text-farmhouse-cream"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-farmhouse-cream">I want to</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setRegisterForm({ ...registerForm, role: 'guest' })}
                      className={`p-4 rounded-sm border transition-colors ${
                        registerForm.role === 'guest'
                          ? 'border-farmhouse-saddle bg-farmhouse-saddle/10 text-farmhouse-saddle'
                          : 'border-farmhouse-subtle text-farmhouse-muted hover:border-farmhouse-stone'
                      }`}
                      data-testid="role-guest"
                    >
                      <span className="block font-medium">Find Stables</span>
                      <span className="text-xs opacity-80">Book for my animals</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegisterForm({ ...registerForm, role: 'host' })}
                      className={`p-4 rounded-sm border transition-colors ${
                        registerForm.role === 'host'
                          ? 'border-farmhouse-saddle bg-farmhouse-saddle/10 text-farmhouse-saddle'
                          : 'border-farmhouse-subtle text-farmhouse-muted hover:border-farmhouse-stone'
                      }`}
                      data-testid="role-host"
                    >
                      <span className="block font-medium">List My Space</span>
                      <span className="text-xs opacity-80">Host animals</span>
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full" 
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        {/* Demo accounts */}
        <div className="mt-6 p-4 bg-farmhouse-paper/50 border border-farmhouse-subtle rounded-md">
          <p className="text-sm text-farmhouse-muted text-center mb-2">Demo Accounts</p>
          <div className="grid grid-cols-3 gap-2 text-xs text-farmhouse-muted">
            <div className="text-center">
              <p className="font-medium text-farmhouse-cream">Guest</p>
              <p>guest@demo.com</p>
            </div>
            <div className="text-center">
              <p className="font-medium text-farmhouse-cream">Host</p>
              <p>host@demo.com</p>
            </div>
            <div className="text-center">
              <p className="font-medium text-farmhouse-cream">Admin</p>
              <p>admin@demo.com</p>
            </div>
          </div>
          <p className="text-xs text-center text-farmhouse-muted mt-2">Password: demo123</p>
        </div>
      </div>
    </div>
  );
}
